import { KioskSettings, KioskDevice, KioskAnalytics } from '../types';

export const mockKioskSettings: KioskSettings[] = [
  {
    id: 'kiosk-settings-1',
    name: 'Main Lobby Kiosk',
    description: 'Settings for the kiosk in the main lobby entrance',
    active: true,
    defaultMode: 'rotation',
    rotation: {
      interval: 15,
      documentIds: ['doc1', 'doc2', 'doc3', 'doc4', 'doc5'],
      transitionEffect: 'fade',
      showMetadata: true,
      autoStart: true
    },
    search: {
      enabled: true,
      autocompleteEnabled: true,
      maxResults: 20,
      searchSections: ['employees', 'directors', 'archives'],
      defaultSection: 'all',
      fuzzyMatchingEnabled: true
    },
    browse: {
      enabled: true,
      sections: {
        employees: true,
        directors: true,
        archives: true
      },
      categoriesEnabled: true,
      gridColumns: {
        mobile: 2,
        tablet: 3,
        desktop: 4
      }
    },
    display: {
      theme: 'system',
      idleTimeout: 300,
      idleReturnToHome: true,
      showClock: true,
      showLogo: true,
      customCss: ''
    },
    lastUpdated: Date.now() / 1000 - 86400 // 1 day ago
  },
  {
    id: 'kiosk-settings-2',
    name: 'Historical Society Display',
    description: 'Settings for the kiosk at the historical society',
    active: true,
    defaultMode: 'browse',
    rotation: {
      interval: 20,
      documentIds: ['doc6', 'doc7', 'doc8', 'doc9', 'doc10'],
      transitionEffect: 'slide',
      showMetadata: true,
      autoStart: false
    },
    search: {
      enabled: true,
      autocompleteEnabled: true,
      maxResults: 30,
      searchSections: ['archives'],
      defaultSection: 'archives',
      fuzzyMatchingEnabled: true
    },
    browse: {
      enabled: true,
      sections: {
        employees: false,
        directors: false,
        archives: true
      },
      categoriesEnabled: true,
      gridColumns: {
        mobile: 2,
        tablet: 3,
        desktop: 4
      }
    },
    display: {
      theme: 'light',
      idleTimeout: 180,
      idleReturnToHome: true,
      showClock: false,
      showLogo: true,
      customCss: ''
    },
    lastUpdated: Date.now() / 1000 - 172800 // 2 days ago
  },
  {
    id: 'kiosk-settings-3',
    name: 'Employee Directory',
    description: 'Settings for the employee directory kiosk',
    active: false,
    defaultMode: 'browse',
    rotation: {
      interval: 10,
      documentIds: [],
      transitionEffect: 'fade',
      showMetadata: true,
      autoStart: true
    },
    search: {
      enabled: true,
      autocompleteEnabled: true,
      maxResults: 50,
      searchSections: ['employees', 'directors'],
      defaultSection: 'employees',
      fuzzyMatchingEnabled: true
    },
    browse: {
      enabled: true,
      sections: {
        employees: true,
        directors: true,
        archives: false
      },
      categoriesEnabled: false,
      gridColumns: {
        mobile: 2,
        tablet: 3,
        desktop: 4
      }
    },
    display: {
      theme: 'dark',
      idleTimeout: 120,
      idleReturnToHome: true,
      showClock: true,
      showLogo: true,
      customCss: ''
    },
    lastUpdated: Date.now() / 1000 - 259200 // 3 days ago
  }
];

export const mockKioskDevices: KioskDevice[] = [
  {
    id: 'device-1',
    name: 'Lobby iPad',
    description: 'iPad in the main lobby',
    location: 'Main Lobby',
    model: 'iPad Pro 12.9"',
    serialNumber: 'IPAD12345678',
    ipAddress: '192.168.1.101',
    macAddress: '00:1A:2B:3C:4D:5E',
    lastPing: Date.now() / 1000 - 300, // 5 minutes ago
    status: 'online',
    settingsId: 'kiosk-settings-1',
    lastUpdated: Date.now() / 1000 - 86400, // 1 day ago
    notes: 'Mounted on the wall near the entrance',
    orientation: 'landscape',
    screenSize: '2732x2048',
    osVersion: 'iOS 16.5',
    batteryLevel: 100,
    storageUsed: 45,
    tags: ['lobby', 'entrance', 'public']
  },
  {
    id: 'device-2',
    name: 'Historical Society iPad',
    description: 'iPad at the historical society',
    location: 'Historical Society Building',
    model: 'iPad Air 4',
    serialNumber: 'IPAD87654321',
    ipAddress: '192.168.1.102',
    macAddress: '00:5E:4D:3C:2B:1A',
    lastPing: Date.now() / 1000 - 600, // 10 minutes ago
    status: 'online',
    settingsId: 'kiosk-settings-2',
    lastUpdated: Date.now() / 1000 - 172800, // 2 days ago
    notes: 'Placed on the reception desk',
    orientation: 'landscape',
    screenSize: '2360x1640',
    osVersion: 'iOS 16.5',
    batteryLevel: 85,
    storageUsed: 38,
    tags: ['historical', 'society', 'public']
  },
  {
    id: 'device-3',
    name: 'HR Department iPad',
    description: 'iPad in the HR department',
    location: 'HR Department, 3rd Floor',
    model: 'iPad 10th Gen',
    serialNumber: 'IPAD98765432',
    ipAddress: '192.168.1.103',
    macAddress: '00:2B:3C:4D:5E:1A',
    lastPing: Date.now() / 1000 - 86400, // 1 day ago
    status: 'offline',
    settingsId: 'kiosk-settings-3',
    lastUpdated: Date.now() / 1000 - 259200, // 3 days ago
    notes: 'For employee directory access only',
    orientation: 'portrait',
    screenSize: '2160x1620',
    osVersion: 'iOS 16.4',
    batteryLevel: 0,
    storageUsed: 32,
    tags: ['hr', 'employees', 'internal']
  }
];

export const mockKioskAnalytics: KioskAnalytics[] = [
  {
    id: 'analytics-1',
    deviceId: 'device-1',
    date: '2025-05-07',
    views: 127,
    searches: 42,
    interactions: 89,
    popularDocuments: [
      { documentId: 'doc1', views: 28 },
      { documentId: 'doc3', views: 22 },
      { documentId: 'doc5', views: 19 },
      { documentId: 'doc2', views: 15 },
      { documentId: 'doc4', views: 12 }
    ],
    popularSearchTerms: [
      { term: 'bank', count: 12 },
      { term: 'director', count: 9 },
      { term: 'photo', count: 7 },
      { term: 'history', count: 6 },
      { term: 'building', count: 5 }
    ]
  },
  {
    id: 'analytics-2',
    deviceId: 'device-2',
    date: '2025-05-07',
    views: 98,
    searches: 31,
    interactions: 67,
    popularDocuments: [
      { documentId: 'doc7', views: 24 },
      { documentId: 'doc9', views: 19 },
      { documentId: 'doc6', views: 17 },
      { documentId: 'doc8', views: 14 },
      { documentId: 'doc10', views: 11 }
    ],
    popularSearchTerms: [
      { term: 'historical', count: 10 },
      { term: 'society', count: 8 },
      { term: 'archive', count: 7 },
      { term: 'photo', count: 6 },
      { term: 'document', count: 5 }
    ]
  },
  {
    id: 'analytics-3',
    deviceId: 'device-3',
    date: '2025-05-06',
    views: 45,
    searches: 18,
    interactions: 32,
    popularDocuments: [
      { documentId: 'doc11', views: 12 },
      { documentId: 'doc13', views: 10 },
      { documentId: 'doc15', views: 8 },
      { documentId: 'doc12', views: 7 },
      { documentId: 'doc14', views: 5 }
    ],
    popularSearchTerms: [
      { term: 'employee', count: 8 },
      { term: 'director', count: 6 },
      { term: 'profile', count: 5 },
      { term: 'department', count: 4 },
      { term: 'position', count: 3 }
    ]
  }
];

export default {
  settings: mockKioskSettings,
  devices: mockKioskDevices,
  analytics: mockKioskAnalytics
};
